import { useState, useEffect, useRef, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useAsk, ChatMessage } from "./useAsk";
import {
  AskResponse,
  ImmediateActionCard,
  RecommendedActionCard,
} from "../types/ask";
import { ImmediateActionCards } from "./ImmediateActionCards";
import { RecommendedActionCards } from "./RecommendedActionCards";
import { LoadMore } from "@coworker/apprestructured/src/shared/components/LoadMore/LoadMore";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { LoaderIcon } from "@coworker/reusable/Loader";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import ChatIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChatIconPath";
import SparklesIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/SparklesIconPath";
import { useCreateTask } from "../../../hooks/TaskActions/useCreateTask";
import { useStoreId, useUID } from "../../../core/auth/useLoggedInUser";
import { Reasons } from "../constants";
import { mediaCreateTaskFactory } from "../ObjectFactories/TaskFactory";
import {
  getArticlesByProductArticleId,
  getMediasByStoreId,
  getMediasAndArticleCount,
} from "../services/medias.service";
import { insertTaskLink } from "../services/tasks.service";
import { useTranslation } from "@coworker/locales";

const AiAssistantBanner = styled.div`
  cursor: pointer;

  .list-view-item,
  .list-view-item__action {
    background-color: #0058a3; //#003E72;
    border-bottom: none;
  }

  .ai-assistant-list .list-view-item__action {
    padding-inline-end: 30px;
    padding-inline-start: 25px;
  }

  .list-view-item__title {
    color: white;
  }

  .list-view-item__icon-center,
  .list-view-item__control-icon {
    color: white;
  }
`;
const AiAssistantBannerQuestion = styled.div`
  .list-view-item,
  .list-view-item__action {
    background-color: #0058a3;
    border-bottom: none;
  }
  .list-view-item__icon-center,
  .list-view-item__control-icon {
    color: white;
    margin-left: 15px;
  }
  .list-view-item__title {
    color: white;
  }
  width: 95%;
  margin-left: 18px;
`;
const QuickActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const QuickActionButton = styled.button`
  background: var(--grey100);
  border: none;
  border-radius: 24px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  &:hover {
    background: var(--grey200);
  }
`;

const FloatingQuickActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  background-color: #003e72;
  padding: 24px;
`;

const FloatingQuickActionButton = styled.button`
  background: #19507f;
  border: none;
  border-radius: 24px;
  padding: 16px;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  color: #8ca7bf;
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const GlobalFooterStyle = createGlobalStyle`
  .modal-footer.ask-fixa-footer-active {
    background-color: #003E72;
  }
`;

const ChatInputWrapper = styled.div<{ $hasHistory: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  width: 100%;
  background-color: ${({ $hasHistory }) => ($hasHistory ? "#003E72" : "white")};
`;

const ChatInputInner = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--grey300);
  border-radius: 24px;
  padding: 10px 16px;
  &:focus-within {
    border-color: var(--grey700);
  }
  background-color: white;
`;

const ChatInput = styled.input`
  flex: 1;
  border: none;
  font-size: 14px;
  outline: none;
  background: transparent;
`;

const SendButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  display: flex;
  align-items: center;
`;

const ChatHistory = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
  min-height: 300px;
`;

const CopiedToast = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  padding: 0.5rem;
  padding-inline-start: 1.5rem;
  min-height: 2.125rem;
  background: rgb(var(--colour-elevation-3, 17, 17, 17));
  color: rgb(var(--colour-static-white, 255, 255, 255));
  border-radius: 4px;
  border: 1px solid rgb(var(--colour-elevation-1-border, 223, 223, 223));
  margin: 8px 16px;
`;

const EmptyChatBody = styled.div`
  flex: 1;
  min-height: 300px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const LoadingBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--grey700);
  font-size: 14px;
  align-self: flex-start;
  margin-left: 18px;
`;

const AssistantBubble = styled.div`
  background: var(--white);
  padding: 12px 16px;
  font-size: 14px;
  align-self: flex-start;
  max-width: 95%;
  width: 95%;
`;

const ErrorBubble = styled.div`
  background: var(--colour-semantic-negative-subtle, #fce8e8);
  border: 1px solid var(--colour-semantic-negative, #e00751);
  border-radius: 16px 16px 16px 4px;
  padding: 10px 16px;
  font-size: 14px;
  color: var(--colour-semantic-negative, #e00751);
  align-self: flex-start;
  max-width: 80%;
  margin-left: 18px;
`;

const InfoBubble = styled.div`
  background: var(--colour-semantic-negative-subtle, #fce8e8);
  border: 1px solid var(--grey500);
  border-radius: 16px 16px 16px 4px;
  padding: 10px 16px;
  font-size: 14px;
  color: var(--black);
  align-self: flex-start;
  max-width: 80%;
  margin-left: 18px;
`;

const ResultsHeader = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--grey700);
  margin: 12px 0 6px;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 8px;
  overflow-x: auto;
  display: block;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--grey700);
  border-bottom: 2px solid var(--grey200);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 6px 10px;
  border-bottom: 1px solid var(--grey100);
  color: var(--grey900);
`;

const TdNumber = styled(Td)`
  font-variant-numeric: tabular-nums;
  text-align: right;
  font-weight: 600;
`;

const TaskCard = styled.div`
  border: 1px solid var(--grey200);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`;

const TaskTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--grey900);
  margin-bottom: 4px;
`;

const TaskDescription = styled.div`
  font-size: 13px;
  color: var(--grey700);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const TaskAction = styled.div`
  font-size: 13px;
  color: var(--grey900);
  line-height: 1.4;
  background: var(--grey100);
  border-radius: 6px;
  padding: 8px 12px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: var(--grey500);
  cursor: pointer;
  padding: 4px 8px;
  align-self: center;
  text-decoration: underline;
  &:hover {
    color: var(--grey700);
  }
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0 2px;
`;

const SelectionPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
`;

const ClearSelectionButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: var(--grey500);
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover {
    color: var(--colour-semantic-negative, #e00751);
  }
`;

const ChatHeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 4px 16px 0;
`;

const QUICK_ACTIONS = [
  "askFixaSalesStopArticlesString",
  "askFixaPastEDSArticlesString",
  "askFixaHighPriorityArticlesString",
  "askFixaEDSReachingThisWeekString",
  "askFixaEDSReachingThisMonthString",
  "askFixaArticlesNeedingActionString",
  "askFixaAllEDSArticlesString",
  "askFixaOverallStatusString",
  "askFixaUrgentArticlesString",
  "askFixaPastEDSArticlesPaginatedString",
];

function formatColumnName(key: string): string {
  if (key === "total") return "totalString";
  const pascal = key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
  return `askFixa${pascal}String`;
}

function articleKey(item: any) {
  return `${item.product_id ?? ""}__${item.media_name ?? ""}`;
}

const PAGE_SIZE = 10;

function AssistantResponse({
  response,
  onCopy,
}: {
  response: AskResponse;
  selectedArticles: any[];
  onToggleArticle: (item: any) => void;
  onCopy: () => void;
}) {
  const { t } = useTranslation();
  const dataItems = response.data ?? [];
  const columnKeys =
    dataItems.length > 0
      ? Object.keys(dataItems[0]).filter((k) => k !== "sql")
      : [];
  const isArticleData = columnKeys.includes("product_id");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  return (
    <AssistantBubble>
      {response.cards &&
        response.cards.length > 0 &&
        (() => {
          const immediateCards = response.cards.filter(
            (c): c is ImmediateActionCard => c.card_type === "IMMEDIATE_ACTION"
          );
          const recommendedCards = response.cards.filter(
            (c): c is RecommendedActionCard =>
              c.card_type === "RECOMMENDED_ACTION"
          );
          return (
            <>
              {immediateCards.length > 0 && (
                <ImmediateActionCards cards={immediateCards} onCopy={onCopy} />
              )}
              {recommendedCards.length > 0 && (
                <RecommendedActionCards cards={recommendedCards} />
              )}
            </>
          );
        })()}

      {dataItems.length > 0 && !isArticleData && (
        <>
          <ResultsHeader>
            {/* {Math.min(visibleCount, dataItems.length)} of {dataItems.length}{" "}
            results */}
          </ResultsHeader>
          <DataTable>
            <thead>
              <tr>
                {columnKeys.map((key) => (
                  <Th key={key}>{t(formatColumnName(key))}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataItems.slice(0, visibleCount).map((row: any, i: number) => (
                <tr key={i}>
                  {columnKeys.map((key) => {
                    const val = row[key];
                    const isNumeric =
                      val !== null && val !== undefined && !isNaN(Number(val));
                    return isNumeric ? (
                      <TdNumber key={key}>{val}</TdNumber>
                    ) : (
                      <Td key={key}>{val ?? "—"}</Td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </DataTable>
          {visibleCount < dataItems.length && (
            <LoadMore
              totalCount={dataItems.length}
              loadedItemsCount={Math.min(visibleCount, dataItems.length)}
              onLoadMoreClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
            />
          )}
        </>
      )}

      {response.tasks && response.tasks.length > 0 && (
        <>
          <ResultsHeader>Suggested tasks</ResultsHeader>
          {response.tasks.map((task, i) => (
            <TaskCard key={i}>
              <TaskTitle>{task.title}</TaskTitle>
              <TaskDescription>{task.description}</TaskDescription>
              <TaskAction>{task.suggested_action}</TaskAction>
            </TaskCard>
          ))}
        </>
      )}
    </AssistantBubble>
  );
}

function ChatMessageItem({
  message,
  selectedArticles,
  onToggleArticle,
  onCopy,
}: {
  message: ChatMessage;
  selectedArticles: any[];
  onToggleArticle: (item: any) => void;
  onCopy: () => void;
}) {
  const { t } = useTranslation();

  if (message.role === "user") {
    return (
      <AiAssistantBannerQuestion>
        <FixaListView id="ai-assistant-question" className="ai-assistant-list">
          <FixaListViewItem
            control="default"
            leadingIcon={SparklesIconPath}
            title={t(message.text)}
            emphasised
          />
        </FixaListView>
      </AiAssistantBannerQuestion>
    );
  }
  if (
    message.role === "error" ||
    (message.role === "assistant" && message.response.error)
  ) {
    return (
      <ErrorBubble>{t("askFixaOutOfScopeQuestionAnswerString")}</ErrorBubble>
    );
  }
  if (message.role === "too_large") {
    return <InfoBubble>{t("askFixaTooBigDatasetAnswerString")}</InfoBubble>;
  }
  if (message.role === "timeout") {
    return <InfoBubble>{t("askFixaTimeoutString")}</InfoBubble>;
  }
  if (message.role === "empty") {
    return <InfoBubble>{t("askFixaEmptyResponseString")}</InfoBubble>;
  }
  if (message.role !== "assistant") return null;
  return (
    <AssistantResponse
      response={message.response}
      selectedArticles={selectedArticles}
      onToggleArticle={onToggleArticle}
      onCopy={onCopy}
    />
  );
}

export function AskFixa() {
  const askApi = useAsk() as any;
  const ask = askApi.ask as (
    question: string,
    context?: { selectedArticles?: any[] }
  ) => Promise<void>;
  const messages = askApi.messages as ChatMessage[];
  const loading = askApi.loading as boolean;
  const clearHistory = askApi.clearHistory as () => void;
  const appendAssistantSummary = (summary: string) => {
    if (typeof askApi.appendAssistantSummary === "function") {
      askApi.appendAssistantSummary(summary);
      return;
    }
    ask(summary);
  };
  const appendErrorMessage = (text: string) => {
    if (typeof askApi.appendErrorMessage === "function") {
      askApi.appendErrorMessage(text);
      return;
    }
    ask(text);
  };
  const { call: createTask } = useCreateTask();
  const uid = useUID();
  const storeId = useStoreId();
  const [modalOpen, setModalOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [selectedArticles, setSelectedArticles] = useState<any[]>([]);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [copiedVisible, setCopiedVisible] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestMsgRef = useRef<HTMLDivElement>(null);

  const onCopy = useCallback(() => {
    if (copiedTimerRef.current !== null) clearTimeout(copiedTimerRef.current);
    setCopiedVisible(true);
    copiedTimerRef.current = setTimeout(() => setCopiedVisible(false), 3000);
  }, []);
  const { t } = useTranslation();

  const selectedCount = selectedArticles.length;
  const hasText = question.trim().length > 0;
  const canSend = !loading && (hasText || selectedCount > 0);

  const toggleArticle = (item: any) => {
    const key = articleKey(item);
    setSelectedArticles((prev) => {
      const exists = prev.some((a) => articleKey(a) === key);
      return exists
        ? prev.filter((a) => articleKey(a) !== key)
        : [...prev, item];
    });
  };

  const clearSelection = () => setSelectedArticles([]);

  const isRemoveArticleCommand = (input: string) => {
    const normalized = input.trim().toLowerCase();
    return (
      normalized === "remove article" ||
      normalized === "remove product" ||
      normalized.startsWith("remove article ") ||
      normalized.startsWith("remove product ") ||
      (normalized.includes("remove") && normalized.includes("article"))
    );
  };

  const parseProductIdFromCommand = (input: string): string | null => {
    // Extract product ID from commands like "remove product 10375159"
    const match = input
      .trim()
      .toLowerCase()
      .match(/remove\s+(?:product|article)\s+(\d+)/);
    return match ? (match[1] ?? null) : null;
  };

  const createRemoveProductTasksFromProductId = async (productId: string) => {
    if (!uid || !storeId) {
      appendErrorMessage(
        "Unable to create task: missing user or store context. Please refresh and try again."
      );
      return;
    }

    let created = 0;
    const skipped: string[] = [];
    const failed: string[] = [];

    try {
      // Find all medias and articles for this product ID
      const mediasAndCounts = await getMediasAndArticleCount(
        productId,
        storeId
      );
      if (!mediasAndCounts || mediasAndCounts.length === 0) {
        appendErrorMessage(`No articles found for product ID: ${productId}`);
        return;
      }

      const mediasResponse = await getMediasByStoreId(storeId);
      const medias = mediasResponse?.medias || [];

      for (const mediaAndCount of mediasAndCounts) {
        const mediaId = mediaAndCount.mediaId;
        const media = medias.find((m: any) => m.id === mediaId);

        if (!media?.id) {
          skipped.push(`Media ${mediaId}`);
          continue;
        }

        const matchingArticles = await getArticlesByProductArticleId(
          mediaId,
          productId
        );
        if (!matchingArticles || matchingArticles.length === 0) {
          skipped.push(`${media.name} (no articles found)`);
          continue;
        }

        for (const article of matchingArticles) {
          if (!article?.id) {
            skipped.push(`${media.name} - article ${productId}`);
            continue;
          }

          try {
            const articleName = String(
              article.itemName ?? (article as any).item_name ?? ""
            ).trim();
            const articleImageSmall = String(
              article.imageSmall ?? (article as any).image_small ?? ""
            ).trim();
            const itemName = articleName || `Product ${productId}`;
            const task = mediaCreateTaskFactory(
              uid,
              media.id,
              article.id,
              itemName,
              article.supplierNumber,
              article.dateStamp,
              article.nbrArticles || 0,
              false,
              false,
              false,
              false,
              media.name || "",
              Reasons.REMOVE_PRODUCT,
              article.productArticleId || productId,
              storeId,
              "",
              article.itemType || "",
              article.itemColor || "",
              `Auto-created from Ask Fixa chat command: remove product ${productId}`,
              articleImageSmall,
              false,
              ""
            );

            // Ensure media_tasks.item is always populated for ROOM_SETTINGS tasks.
            task.item_name = itemName;
            task.item = {
              measurements: task.item?.measurements || "",
              image_small: articleImageSmall,
            };

            // Assign to current user to ensure task creation does not fail on missing team selection.
            task.assigned_user_id = uid;
            task.state = "ASSIGNED";

            const taskId = await createTask(task, task.images);
            if (!taskId) {
              failed.push(itemName);
              continue;
            }

            await insertTaskLink({
              articleId: article.id,
              mediaId: media.id,
              storeId,
              productArticleId: article.productArticleId || productId,
              taskId,
            } as any);

            created += 1;
          } catch {
            failed.push(`${media.name} - ${productId}`);
          }
        }
      }

      const summaryParts: string[] = [];
      if (created > 0) {
        summaryParts.push(
          `Created ${created} ROOM_SETTINGS task${created === 1 ? "" : "s"} with reason "remove_product" for product ${productId}.`
        );
      }
      if (skipped.length > 0) {
        summaryParts.push(
          `Skipped ${skipped.length} media/article${skipped.length === 1 ? "" : "s"} due to missing mapping.`
        );
      }
      if (failed.length > 0) {
        summaryParts.push(
          `Failed to create ${failed.length} task${failed.length === 1 ? "" : "s"}.`
        );
      }

      appendAssistantSummary(
        summaryParts.length > 0
          ? summaryParts.join(" ")
          : `No tasks were created for product ${productId}.`
      );
    } catch (error) {
      appendErrorMessage(
        `Unable to process remove product for ${productId}. Please try again.`
      );
    }
  };

  const createRemoveProductTasks = async () => {
    if (!uid || !storeId) {
      appendErrorMessage(
        "Unable to create task: missing user or store context. Please refresh and try again."
      );
      return;
    }

    const uniqueSelected = selectedArticles.filter(
      (item, index, arr) =>
        arr.findIndex((x) => articleKey(x) === articleKey(item)) === index
    );

    let created = 0;
    const skipped: string[] = [];
    const failed: string[] = [];

    try {
      const mediasResponse = await getMediasByStoreId(storeId);
      const medias = mediasResponse?.medias || [];

      for (const selected of uniqueSelected) {
        const productArticleId = String(
          selected.product_id ?? selected.productArticleId ?? ""
        ).trim();
        const mediaName = String(
          selected.media_name ?? selected.mediaName ?? ""
        ).trim();
        const itemName =
          String(
            selected.item_name ?? selected.itemName ?? selected.product_id ?? ""
          ).trim() || "article";
        const selectedImageSmall = String(
          selected.image_small ?? selected.imageSmall ?? ""
        ).trim();

        if (!productArticleId || !mediaName) {
          skipped.push(itemName);
          continue;
        }

        const media = medias.find(
          (m: any) =>
            String(m.name || "")
              .trim()
              .toLowerCase() === mediaName.toLowerCase()
        );

        if (!media?.id) {
          skipped.push(itemName);
          continue;
        }

        const matchingArticles = await getArticlesByProductArticleId(
          media.id,
          productArticleId
        );
        const article = matchingArticles?.[0];

        if (!article?.id) {
          skipped.push(itemName);
          continue;
        }

        try {
          const articleName = String(
            article.itemName ?? (article as any).item_name ?? ""
          ).trim();
          const articleImageSmall = String(
            article.imageSmall ?? (article as any).image_small ?? ""
          ).trim();
          const resolvedItemName = articleName || itemName;
          const resolvedImageSmall = articleImageSmall || selectedImageSmall;

          const task = mediaCreateTaskFactory(
            uid,
            media.id,
            article.id,
            resolvedItemName,
            article.supplierNumber,
            article.dateStamp,
            article.nbrArticles || 0,
            false,
            false,
            false,
            false,
            media.name || mediaName,
            Reasons.REMOVE_PRODUCT,
            article.productArticleId || productArticleId,
            storeId,
            "",
            article.itemType || "",
            article.itemColor || "",
            "Auto-created from Ask Fixa chat command: remove article",
            resolvedImageSmall,
            false,
            ""
          );

          // Ensure media_tasks.item is always populated for ROOM_SETTINGS tasks.
          task.item_name = resolvedItemName;
          task.item = {
            measurements: task.item?.measurements || "",
            image_small: resolvedImageSmall,
          };

          // Assign to current user to ensure task creation does not fail on missing team selection.
          task.assigned_user_id = uid;
          task.state = "ASSIGNED";

          const taskId = await createTask(task, task.images);
          if (!taskId) {
            failed.push(itemName);
            continue;
          }

          await insertTaskLink({
            articleId: article.id,
            mediaId: media.id,
            storeId,
            productArticleId: article.productArticleId || productArticleId,
            taskId,
          } as any);

          created += 1;
        } catch {
          failed.push(itemName);
        }
      }

      const summaryParts: string[] = [];
      if (created > 0) {
        summaryParts.push(
          `Created ${created} ROOM_SETTINGS task${created === 1 ? "" : "s"} with reason "remove_product".`
        );
      }
      if (skipped.length > 0) {
        summaryParts.push(
          `Skipped ${skipped.length} article${skipped.length === 1 ? "" : "s"} due to missing media/article mapping.`
        );
      }
      if (failed.length > 0) {
        summaryParts.push(
          `Failed to create ${failed.length} task${failed.length === 1 ? "" : "s"}.`
        );
      }

      appendAssistantSummary(
        summaryParts.length > 0
          ? summaryParts.join(" ")
          : "No tasks were created for the selected articles."
      );
    } catch {
      appendErrorMessage(
        "Unable to create remove-product tasks right now. Please try again."
      );
    }
  };

  useEffect(() => {
    if (latestMsgRef.current) {
      latestMsgRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setIsAtBottom(false);
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!modalOpen) return;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.classList?.contains("sheets__content-wrapper")) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      setIsAtBottom(scrollHeight - scrollTop - clientHeight < 20);
    };

    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [modalOpen]);

  const handleAsk = async (q?: string) => {
    const rawText = (q ?? question).trim();

    // Check if this is a "remove product/article" command with a product ID
    if (isRemoveArticleCommand(rawText)) {
      const productId = parseProductIdFromCommand(rawText);
      if (productId) {
        // Remove product by ID directly (no pre-selected articles needed)
        setQuestion("");
        await createRemoveProductTasksFromProductId(productId);
        return;
      } else if (selectedCount > 0) {
        // Remove with pre-selected articles
        setQuestion("");
        await createRemoveProductTasks();
        setSelectedArticles([]);
        return;
      }
    }

    let text: string;
    if (selectedCount > 0) {
      // Build a human-readable list of the selected articles so the LLM
      // scopes its query to those specific items.
      const articleList = selectedArticles
        .map(
          (a) =>
            `"${a.item_name ?? a.product_id}" (product ID: ${a.product_id ?? "—"}, media: ${a.media_name ?? "—"})`
        )
        .join(", ");

      if (rawText) {
        // e.g. "stock" → "Show stock for these articles: "Chair" (product ID: …)"
        text = `${rawText} for the following selected articles: ${articleList}`;
      } else {
        text = `Tell me more about these articles: ${articleList}`;
      }
    } else {
      text = rawText;
    }

    if (!text) return;
    setQuestion("");
    const context = selectedCount > 0 ? { selectedArticles } : undefined;
    setSelectedArticles([]);
    await ask(text, context);
  };

  const placeholder =
    selectedCount > 0
      ? `Ask a follow-up about the ${selectedCount} selected article${selectedCount === 1 ? "" : "s"}…`
      : t("askFixaAnythingString");

  const hasHistory = messages.length > 0;

  return (
    <>
      <GlobalFooterStyle />
      <AiAssistantBanner onClick={() => setModalOpen(true)}>
        <FixaListView id="ai-assistant-banner" className="ai-assistant-list">
          <FixaListViewItem
            control="navigational"
            controlIcon="arrowRight"
            leadingIcon={SparklesIconPath}
            title={`AI ${t("askFixaAssistantString")}`}
            emphasised
          />
        </FixaListView>
      </AiAssistantBanner>
      <FixaModal
        visible={modalOpen}
        handleCloseBtn={() => setModalOpen(false)}
        focusLockProps={{ locked: false }}
      >
        <FixaSheets
          labelledById="ask-fixa-modal-header"
          size="medium"
          header={
            <FixaModalHeader
              id="ask-fixa-modal-header"
              ariaCloseTxt="Close"
              title=""
            />
          }
          footer={
            <FixaModalFooter
              className={
                hasHistory && isAtBottom ? "ask-fixa-footer-active" : undefined
              }
            >
              {selectedCount > 0 && (
                <SelectionBar>
                  <SelectionPill>
                    ✓ {selectedCount} article{selectedCount === 1 ? "" : "s"}{" "}
                    selected
                  </SelectionPill>
                  <ClearSelectionButton type="button" onClick={clearSelection}>
                    Clear selection
                  </ClearSelectionButton>
                </SelectionBar>
              )}
              <ChatInputWrapper $hasHistory={hasHistory && isAtBottom}>
                <ChatInputInner>
                  <FixaSSRIcon paths={ChatIconPath} />
                  <ChatInput
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={placeholder}
                    disabled={loading}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAsk();
                      }
                    }}
                  />
                </ChatInputInner>
                <SendButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAsk();
                  }}
                  disabled={!canSend}
                  aria-label="Send"
                >
                  ➤
                </SendButton>
              </ChatInputWrapper>
            </FixaModalFooter>
          }
        >
          {hasHistory && (
            <ChatHeaderRow>
              <ClearButton onClick={clearHistory}>
                {t("clearHistoryString")}
              </ClearButton>
            </ChatHeaderRow>
          )}

          {hasHistory ? (
            <>
              <ChatHistory>
                {messages.map((msg, index) => (
                  <div
                    key={msg.id}
                    ref={
                      index === messages.length - 1 ? latestMsgRef : undefined
                    }
                  >
                    <ChatMessageItem
                      message={msg}
                      selectedArticles={selectedArticles}
                      onToggleArticle={toggleArticle}
                      onCopy={onCopy}
                    />
                  </div>
                ))}
                {loading && (
                  <LoadingBubble>
                    <LoaderIcon />
                    <span>{t("askFixaThinkingString")}</span>
                  </LoadingBubble>
                )}
              </ChatHistory>
              {!loading && (
                <FloatingQuickActionsContainer>
                  {QUICK_ACTIONS.map((action) => (
                    <FloatingQuickActionButton
                      key={action}
                      onClick={() => handleAsk(t(action))}
                    >
                      {t(action)}
                    </FloatingQuickActionButton>
                  ))}
                </FloatingQuickActionsContainer>
              )}
            </>
          ) : (
            <EmptyChatBody>
              {loading ? (
                <LoadingBubble>
                  <LoaderIcon />
                  <span>{t("askFixaThinkingString")}</span>
                </LoadingBubble>
              ) : (
                <QuickActionsContainer>
                  {QUICK_ACTIONS.map((action) => (
                    <QuickActionButton
                      key={action}
                      onClick={() => handleAsk(t(action))}
                    >
                      {t(action)}
                    </QuickActionButton>
                  ))}
                </QuickActionsContainer>
              )}
            </EmptyChatBody>
          )}
          {copiedVisible && (
            <CopiedToast>{t("artileNumberCopiedToCliboardString")}</CopiedToast>
          )}
        </FixaSheets>
      </FixaModal>
    </>
  );
}
